import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, Search, Flame, Thermometer, Snowflake } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { getCampaign, getCampaignRecipients } from '../../lib/campaignsApi';

// ─── Atoms ────────────────────────────────────────────────────────────
const Stat = ({ label, value, sub, testid, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testid}
    data-active={active ? 'true' : 'false'}
    className={`text-left border p-4 transition ${
      active
        ? 'border-[#00D4FF] bg-[#00D4FF]/5'
        : 'border-white/10 bg-[#0A0A0A] hover:border-white/25'
    } ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
  >
    <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">{label}</div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
  </button>
);

const StatusBadge = ({ status }) => {
  const map = {
    sent: 'text-emerald-400',
    failed: 'text-red-400',
    queued: 'text-white/50',
    pending: 'text-white/50',
  };
  return <span className={`text-xs ${map[status] || 'text-white/50'}`}>{status || '\u2014'}</span>;
};

const LeadBadge = ({ status, score }) => {
  if (status === 'Hot') {
    return (
      <span data-testid={`lead-badge-hot`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-red-500/15 text-red-400 border border-red-500/30">
        <Flame className="w-3 h-3" /> Hot <span className="text-red-300/70">{score}</span>
      </span>
    );
  }
  if (status === 'Warm') {
    return (
      <span data-testid={`lead-badge-warm`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30">
        <Thermometer className="w-3 h-3" /> Warm <span className="text-amber-300/70">{score}</span>
      </span>
    );
  }
  return (
    <span data-testid={`lead-badge-cold`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-white/5 text-white/55 border border-white/10">
      <Snowflake className="w-3 h-3" /> Cold <span className="text-white/35">{score}</span>
    </span>
  );
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '\u2014';

// Strip Resend's "email." prefix and color by event type. Reuses the same
// palette as StatusBadge so the table reads consistently.
const LastEventBadge = ({ event }) => {
  if (!event) return <span className="text-white/35 text-xs">{'\u2014'}</span>;
  const clean = String(event).replace(/^email\./, '');
  const map = {
    delivered: 'text-emerald-400',
    opened: 'text-emerald-400',
    clicked: 'text-[#00D4FF]',
    bounced: 'text-red-400',
    complained: 'text-red-400',
  };
  return (
    <span
      data-testid={`last-event-${clean}`}
      className={`text-xs ${map[clean] || 'text-white/55'}`}
    >
      {clean}
    </span>
  );
};

// ─── Filter buckets ───────────────────────────────────────────────────
// Engagement filter (status of activity / delivery)
const ENGAGEMENT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'opened_2_plus', label: 'Opened 2+' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'clicked_no_reply', label: 'Clicked · no RFQ' },
  { key: 'never_opened', label: 'Never opened' },
  { key: 'failed', label: 'Failed' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
];

// Lead Status filter (Hot / Warm / Cold)
const LEAD_FILTERS = [
  { key: 'all', label: 'All leads' },
  { key: 'hot', label: 'Hot' },
  { key: 'warm', label: 'Warm' },
  { key: 'warm_hot', label: 'Warm + Hot' },
  { key: 'cold', label: 'Cold' },
];

export const AdminCampaignDetail = () => {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');     // engagement bucket
  const [leadFilter, setLeadFilter] = useState('all'); // hot/warm/cold
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  // Debounce the search box so we don't hit the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // The backend supports a single `filter` query param. Engagement and Lead
  // dropdowns share that param: whichever was changed last wins. If both are
  // "all", we send "all". Otherwise we send the non-"all" one (lead takes
  // precedence when both are set explicitly).
  const effectiveFilter = useMemo(() => {
    if (leadFilter !== 'all') return leadFilter;
    return filter;
  }, [filter, leadFilter]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, r] = await Promise.all([
        getCampaign(id),
        getCampaignRecipients(id, { filter: effectiveFilter, search: debounced }),
      ]);
      setCampaign(c.data);
      const data = r.data || {};
      setRows(Array.isArray(data) ? data : (data.rows || []));
      setCounts(data.counts || {});
    } catch (e) {
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [id, effectiveFilter, debounced]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while sending
  useEffect(() => {
    if (campaign?.status === 'sending') {
      const i = setInterval(load, 4000);
      return () => clearInterval(i);
    }
  }, [campaign?.status, load]);

  const handleExport = () => {
    const adminToken = localStorage.getItem('luxortek_admin_token');
    const qs = new URLSearchParams({ filter: effectiveFilter }).toString();
    const url = `/api/admin/campaigns/${id}/export.csv?${qs}`;
    fetch(url, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const suffix = effectiveFilter && effectiveFilter !== 'all' ? `-${effectiveFilter}` : '';
        a.download = `campaign-${id.slice(0, 8)}${suffix}.csv`;
        a.click();
      })
      .catch(() => toast.error('Export failed'));
  };

  if (loading && !campaign) return <div className="p-6 text-white/50 text-sm">Loading…</div>;
  if (!campaign) return <div className="p-6 text-white/50 text-sm">Campaign not found.</div>;

  const total = counts.all ?? campaign.recipient_count ?? campaign.sent_count ?? 0;
  const opens = counts.opened ?? campaign.unique_open_count ?? 0;
  const clicks = counts.clicked ?? campaign.unique_click_count ?? 0;
  const unsubs = counts.unsubscribed ?? campaign.unsubscribe_count ?? 0;
  const delivered = campaign.sent_count ?? 0;
  const failed = counts.failed ?? campaign.failed_count ?? 0;
  const openRate = total ? Math.round((opens / total) * 1000) / 10 : 0;
  const clickRate = total ? Math.round((clicks / total) * 1000) / 10 : 0;

  // Clicking a metric card sets the engagement filter (and resets lead filter).
  const setBucket = (key) => {
    setFilter((cur) => (cur === key ? 'all' : key));
    setLeadFilter('all');
  };

  return (
    <div className="p-6" data-testid="campaign-detail-page">
      <Toaster position="top-right" theme="dark" />

      <Link to="/admin/campaigns" className="inline-flex items-center gap-1 text-white/40 hover:text-white text-sm mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to Campaigns
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">{campaign.name}</h1>
          <div className="text-white/60 text-sm">{campaign.subject}</div>
          <div className="text-xs text-white/40 mt-2">
            Status: <span className="uppercase tracking-wider">{campaign.status}</span>
            {campaign.send_finished_at && <> &middot; Sent {fmt(campaign.send_finished_at)}</>}
          </div>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'sending' && (
            <button onClick={load} className="btn-secondary inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" /> Sending… {campaign.progress_pct || 0}%
            </button>
          )}
          <button onClick={load} className="btn-secondary inline-flex items-center gap-2" data-testid="refresh-campaign">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={handleExport} className="btn-secondary inline-flex items-center gap-2" data-testid="export-campaign-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Clickable metric cards — clicking a card filters the recipients list. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="campaign-stats">
        <Stat
          testid="stat-recipients"
          label="Recipients"
          value={total}
          active={filter === 'all'}
          onClick={() => setBucket('all')}
        />
        <Stat
          testid="stat-delivered"
          label="Delivered"
          value={delivered}
          sub={`${failed} failed`}
          active={filter === 'failed'}
          onClick={() => setBucket('failed')}
        />
        <Stat
          testid="stat-opens"
          label="Opens"
          value={opens}
          sub={`${openRate}% open rate`}
          active={filter === 'opened'}
          onClick={() => setBucket('opened')}
        />
        <Stat
          testid="stat-clicks"
          label="Clicks"
          value={clicks}
          sub={`${clickRate}% click rate`}
          active={filter === 'clicked'}
          onClick={() => setBucket('clicked')}
        />
        <Stat
          testid="stat-unsubs"
          label="Unsubscribes"
          value={unsubs}
          active={filter === 'unsubscribed'}
          onClick={() => setBucket('unsubscribed')}
        />
      </div>

      {/* Lead temperature cards */}
      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="lead-stats">
        <Stat
          testid="stat-hot"
          label="Hot leads"
          value={counts.hot ?? 0}
          sub="Clicked + RFQ"
          active={leadFilter === 'hot'}
          onClick={() => { setLeadFilter((c) => c === 'hot' ? 'all' : 'hot'); setFilter('all'); }}
        />
        <Stat
          testid="stat-warm"
          label="Warm leads"
          value={counts.warm ?? 0}
          sub="Opens / Clicks"
          active={leadFilter === 'warm'}
          onClick={() => { setLeadFilter((c) => c === 'warm' ? 'all' : 'warm'); setFilter('all'); }}
        />
        <Stat
          testid="stat-cold"
          label="Cold leads"
          value={counts.cold ?? 0}
          sub="No engagement"
          active={leadFilter === 'cold'}
          onClick={() => { setLeadFilter((c) => c === 'cold' ? 'all' : 'cold'); setFilter('all'); }}
        />
      </div>

      {/* Filter row: engagement bucket + lead status + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-semibold tracking-widest uppercase text-white/50">Engagement</label>
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setLeadFilter('all'); }}
            data-testid="engagement-filter"
            className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#00D4FF] outline-none"
          >
            {ENGAGEMENT_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}{counts[f.key] !== undefined ? ` (${counts[f.key]})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-[10px] font-semibold tracking-widest uppercase text-white/50">Lead status</label>
          <select
            value={leadFilter}
            onChange={(e) => { setLeadFilter(e.target.value); setFilter('all'); }}
            data-testid="lead-filter"
            className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#00D4FF] outline-none"
          >
            {LEAD_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}{counts[f.key] !== undefined ? ` (${counts[f.key]})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 flex-1 md:ml-auto md:max-w-sm">
          <Search className="w-4 h-4 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, company…"
            data-testid="recipient-search"
            className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 w-full focus:border-[#00D4FF] outline-none"
          />
        </div>
      </div>

      <div className="text-xs text-white/40 mb-3" data-testid="recipient-result-count">
        Showing {rows.length} {rows.length === 1 ? 'recipient' : 'recipients'}
        {effectiveFilter !== 'all' && (
          <> &middot; filter: <span className="text-white/70">{effectiveFilter.replace(/_/g, ' ')}</span></>
        )}
      </div>

      <div className="border border-white/10 overflow-x-auto" data-testid="recipients-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white/55 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name / Company</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last event</th>
              <th className="text-left px-4 py-3">Opens</th>
              <th className="text-left px-4 py-3">Last opened</th>
              <th className="text-left px-4 py-3">Clicks</th>
              <th className="text-left px-4 py-3">Last clicked</th>
              <th className="text-left px-4 py-3">Bounce reason</th>
              <th className="text-left px-4 py-3">Lead</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-6 text-white/40 text-center">No recipients match this filter.</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={`${r.email}-${idx}`} className="border-t border-white/5" data-testid={`recipient-row-${idx}`}>
                <td className="px-4 py-3">
                  <div className="text-white">{r.name || '\u2014'}</div>
                  <div className="text-white/45 text-xs">{r.company || '\u2014'}</div>
                </td>
                <td className="px-4 py-3 text-[#00D4FF]">
                  {r.email}
                  {r.unsubscribed && (
                    <span className="ml-2 text-[10px] text-red-400 uppercase tracking-wider">unsub</span>
                  )}
                </td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3" data-testid={`recipient-last-event-${idx}`}>
                  <LastEventBadge event={r.last_event} />
                </td>
                <td className="px-4 py-3 text-white/70">
                  {r.open_count > 0 ? <span className="text-emerald-400">{r.open_count}×</span> : '\u2014'}
                </td>
                <td className="px-4 py-3 text-white/50 text-xs">{fmt(r.last_opened_at)}</td>
                <td className="px-4 py-3 text-white/70">
                  {r.click_count > 0 ? <span className="text-[#00D4FF]">{r.click_count}×</span> : '\u2014'}
                </td>
                <td className="px-4 py-3 text-white/50 text-xs">{fmt(r.last_clicked_at)}</td>
                <td className="px-4 py-3 text-xs" data-testid={`recipient-bounce-reason-${idx}`}>
                  {r.bounce_reason
                    ? <span className="text-red-400" title={r.bounce_reason}>{r.bounce_reason.length > 50 ? r.bounce_reason.slice(0, 50) + '\u2026' : r.bounce_reason}</span>
                    : <span className="text-white/30">{'\u2014'}</span>}
                </td>
                <td className="px-4 py-3">
                  <LeadBadge status={r.engagement_status} score={r.engagement_score} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
